//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { setClassNameInDefns } from '../../common/definitionHelper';
import { LayoutContext, AppContext } from '../../common/contexts';
// import { AFITD_DOMAIN } from '../../system/constants';
import {
    CONFIRM_DIALOG_MESSAGE_CANCEL,
    CONFIRM_DIALOG_TITLE_CANCEL,
    EMP_BASE_URL,
    SNACK_BAR_MESSAGE_TEXT_MODERATION_CHECK_FAILED,
    SNACK_BAR_MESSAGE_URL_SAVED,
} from '../../common/constants';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { checkUrlName, updateEmployerData } from '../../common/services/empMySettingsServices';
import { moderateUserTextInput } from '../../common/sightEngineHelper';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';
import AfitdRouterPrompt from '../../common/elemental/AfitdRouterPrompt';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import _get from 'lodash/get';
// import _toLower from 'lodash/toLower';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        textFieldDisabled: { backgroundColor: '#F0F2F6' },
        buttonBox: { display: 'flex', justifyContent: 'flex-end', padding: '10px 0' },
        heading: { fontWeight: 'bold', paddingBottom: 20 },
        url: { color: 'rgba(17, 30, 20, 0.4)', padding: '5px 0' },
        linkFieldContainer: { display: 'flex', marginTop: 30 },
        linkURLFieldContainer: { paddingRight: '5px', width: 270 },
        linkUserNameFieldContainer: { flex: 1 },
        rightMarginToLeftButton: { marginRight: 16 },
        errorMessage: { color: 'red', fontStyle: 'italic' },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        urlName,
        isDirty,
        // setIsDirty,
        handleSaveButton,
        // currentEmployer,
        error,
        props,
        badWords,
        clearOutBadWords,
        openBadWordsDialog,
        saveLoading,
        handleChangeUserName,
        confirmModalOpen,
        setConfirmModalOpen,
        handleClickConfirmCancelModalYesButton,
    } = params;
    const { textFieldDisabled: textFieldDisabledCls, rightMarginToLeftButton: rightMarginToLeftButtonCls } = classes;
    const { isAdmin } = props;

    const badWordsList = badWords?.profanity?.join(', ');
    // const isDirty = _toLower(urlName) !== _toLower(currentEmployer?.urlName || '');
    // const isDirty = _toLower(urlName) !== _toLower(currentUser.urlName || '') && !checkSpaceInString(urlName);
    // const isModalButtonActive = urlName === (currentEmployer.urlName || '');

    const errorMessageIsHidden = !error;

    const defns = {
        // LINK SECTION
        heading: { variant: 'h6', value: `Create your own A Foot in the Door URL!` },
        insText: { variant: 'body1' },
        linkUrlField: {
            placeholder: `${EMP_BASE_URL}/`,
            disabled: true,
            InputProps: {
                className: textFieldDisabledCls,
            },
        },
        url: { variant: 'body2' },
        linkUserNameField: {
            disabled: !isAdmin,
            placeholder: 'User Name',
            value: urlName,
            onChange: handleChangeUserName,
        },
        myLinkCancelButton: {
            disabled: !isDirty,
            onClick: () => {
                setConfirmModalOpen(true);
            },
            className: rightMarginToLeftButtonCls,
        },
        myLinkSaveButton: { disabled: !isDirty, onClick: handleSaveButton, color: 'primary', loading: saveLoading },
        errorMessage: { hidden: errorMessageIsHidden },
        cancelModal: {
            title: CONFIRM_DIALOG_TITLE_CANCEL,
            onClose: () => setConfirmModalOpen(false),
            onConfirm: handleClickConfirmCancelModalYesButton,
            open: confirmModalOpen,
            message: CONFIRM_DIALOG_MESSAGE_CANCEL,
        },
        badWordsDialog: {
            title: 'Inappropriate Text is not Allowed!',
            children: (
                <AfitdTypography>
                    The word(s): <b>{badWordsList} </b>
                    does not meet our community guidelines. Inappropriate content cannot be saved to this platform.
                </AfitdTypography>
            ),
            open: openBadWordsDialog,
            onClose: () => clearOutBadWords(),
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const Link = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();
    const appContext = useContext(AppContext);
    const { currentEmployer, employerId } = appContext;
    const layoutContext = useContext(LayoutContext);
    const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [urlName, setUrlName] = useState('');
    const [error, setError] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [openBadWordsDialog, setOpenBadWordsDialog] = useState(false);
    const [badWords, setBadWords] = useState({});
    const [saveLoading, setSaveLoading] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        setUrlName(currentEmployer?.urlName || '');
    }, [currentEmployer]);

    //---------------------------------------------------------------------
    // Event handlers
    //---------------------------------------------------------------------
    const handleClickConfirmCancelModalYesButton = () => {
        setUrlName(currentEmployer?.urlName || '');
        setIsDirty(false);
    };

    const handleSaveButton = async () => {
        setSaveLoading(true);
        const response = await moderateUserTextInput({ text: JSON.stringify(urlName) });
        setSaveLoading(false);

        if (!response) {
            setSnackbarErrorMessage(SNACK_BAR_MESSAGE_TEXT_MODERATION_CHECK_FAILED);
            // setSaveLoading(false);
        } else if (response?.profanity?.length > 0 || response?.personal?.length > 0 || response?.link?.length > 0) {
            setOpenBadWordsDialog(true);
            setBadWords(response);
        } else {
            setSaveLoading(true);
            const exists = await checkUrlName({ urlName });
            !exists &&
                (await updateEmployerData({
                    data: { ...currentEmployer, urlName },
                    employerId,
                    setSnackbarMessage,
                    setSnackbarErrorMessage,
                    snackBarMessage: SNACK_BAR_MESSAGE_URL_SAVED,
                }));
            setError(exists);
            setSaveLoading(false);
            setIsDirty(false);
        }
    };
    const clearOutBadWords = () => {
        // eslint-disable-next-line no-unused-expressions
        badWords?.profanity?.forEach(badWord => {
            badWord.toLowerCase();

            const regEx = new RegExp(badWord, 'ig');

            if (urlName.toLowerCase().includes(badWord)) {
                const filteredUserName = urlName.replace(regEx, '');
                setUrlName(filteredUserName);
            }
        });
        setOpenBadWordsDialog(false);
        // setBadWords({});
    };

    const handleChangeUserName = evt => {
        setError(null);
        const { value } = evt.target;
        !value.match(/[^A-Za-z0-9-_]/) && setUrlName(value);
        setIsDirty(true);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        urlName,
        isDirty,
        // setIsDirty,
        handleSaveButton,
        // currentEmployer,
        error,
        props,
        openBadWordsDialog,
        badWords,
        clearOutBadWords,
        saveLoading,
        handleChangeUserName,
        confirmModalOpen,
        setConfirmModalOpen,
        handleClickConfirmCancelModalYesButton,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div>
            <AfitdRouterPrompt when={isDirty} />
            <AfitdTypography {...defns.heading} />
            <AfitdTypography {...defns.insText}>
                Sharing your brand's custom URL will help attract top talent from around the world. This link will be visible to the public, so please select a URL that best
                represents your business.
            </AfitdTypography>

            <div {...defns.linkFieldContainer}>
                <div {...defns.linkURLFieldContainer}>
                    <AfitdTextField {...defns.linkUrlField} />
                </div>
                <div {...defns.linkUserNameFieldContainer}>
                    <AfitdTextField {...defns.linkUserNameField} />
                </div>
            </div>

            <AfitdTypography {...defns.url}>
                {`Your Public URL is: ${EMP_BASE_URL}/${urlName}`}
                <span {...defns.errorMessage}> This URL already exists, please enter a unique URL</span>
            </AfitdTypography>

            <div className={classes.buttonBox}>
                <AfitdButton {...defns.myLinkCancelButton}>Cancel</AfitdButton>
                <AfitdButton {...defns.myLinkSaveButton}>Save Changes</AfitdButton>
            </div>
            <AfitdConfirmDialog {...defns.cancelModal} />
            <AfitdMessageDialog {...defns.badWordsDialog} />
        </div>
    );
};

export default Link;
